/**
 * Timezone helpers for the Communications admin (DST-aware Europe/Amsterdam).
 *
 * Why Luxon: native `new Date()` parsing doesn't reliably understand "this
 * datetime, in this named zone" -- it interprets ISO strings as UTC or local
 * machine time depending on whether an offset is present, which silently
 * drifts an hour every six months as Amsterdam moves between CET (UTC+1) and
 * CEST (UTC+2). Luxon's `DateTime.fromISO(local, { zone })` does the right
 * thing for both.
 */

import { DateTime } from "luxon";

export const AMSTERDAM_ZONE = "Europe/Amsterdam";

/**
 * Convert an Amsterdam-local ISO string (no offset, e.g. "2026-05-12T18:30")
 * to UTC milliseconds. Returns NaN if the input is invalid.
 */
export function amsterdamLocalToUtcMs(localIso: string): number {
  if (!localIso) return NaN;
  const dt = DateTime.fromISO(localIso, { zone: AMSTERDAM_ZONE });
  if (!dt.isValid) return NaN;
  return dt.toMillis();
}

/**
 * Convert UTC milliseconds to an Amsterdam-local ISO string suitable for
 * `<input type="datetime-local">` (minute precision, no seconds, no offset).
 */
export function utcMsToAmsterdamLocal(utcMs: number): string {
  if (!Number.isFinite(utcMs)) return "";
  const dt = DateTime.fromMillis(utcMs, { zone: "utc" }).setZone(
    AMSTERDAM_ZONE
  );
  if (!dt.isValid) return "";
  // toFormat without offset, matches `<input type="datetime-local">` value format.
  return dt.toFormat("yyyy-LL-dd'T'HH:mm");
}

/**
 * Format UTC ms as a human-readable Amsterdam local string with offset and
 * DST abbreviation (e.g. "12 May 2026, 18:30 CEST").
 */
export function formatAmsterdamHuman(utcMs: number): string {
  if (!Number.isFinite(utcMs)) return "";
  const dt = DateTime.fromMillis(utcMs, { zone: "utc" }).setZone(
    AMSTERDAM_ZONE
  );
  if (!dt.isValid) return "";
  return dt.toFormat("d LLL yyyy, HH:mm 'CET/CEST'");
}

/**
 * Human-readable "in N hours" / "N hours ago" string from now to the given
 * UTC ms, computed in the Amsterdam zone for consistency.
 */
export function relativeFromNow(utcMs: number): string {
  if (!Number.isFinite(utcMs)) return "";
  const target = DateTime.fromMillis(utcMs, { zone: "utc" }).setZone(
    AMSTERDAM_ZONE
  );
  const now = DateTime.now().setZone(AMSTERDAM_ZONE);
  const diff = target.diff(now, ["days", "hours", "minutes"]).toObject();
  const days = Math.floor(diff.days || 0);
  const hours = Math.floor(diff.hours || 0);
  const minutes = Math.floor(diff.minutes || 0);
  const isPast = utcMs < Date.now();
  const absDays = Math.abs(days);
  const absHours = Math.abs(hours);
  const absMinutes = Math.abs(minutes);
  const parts: string[] = [];
  if (absDays > 0) parts.push(`${absDays}d`);
  if (absHours > 0) parts.push(`${absHours}h`);
  if (parts.length === 0) parts.push(`${absMinutes}m`);
  return isPast ? `${parts.join(" ")} ago` : `in ${parts.join(" ")}`;
}

/**
 * Status of a campaign relative to now.
 * - upcoming: startAt is in the future
 * - active: now is between start and end (inclusive)
 * - ended: endAt is in the past
 * - inactive: explicitly toggled off (overrides the above when isActive=false)
 */
export type CampaignWindowStatus = "upcoming" | "active" | "ended" | "inactive";

export function computeCampaignStatus(
  startAtMs: number,
  endAtMs: number,
  isActive: boolean,
  now: number = Date.now()
): CampaignWindowStatus {
  if (!isActive) return "inactive";
  if (now < startAtMs) return "upcoming";
  if (now > endAtMs) return "ended";
  return "active";
}

/**
 * Status of an event template relative to now. Unlike campaigns the schedule
 * window is OPTIONAL - if both start/end are null/undefined the event is
 * always-on while `isActive` is true.
 */
export function computeEventTemplateStatus(
  startAtMs: number | null | undefined,
  endAtMs: number | null | undefined,
  isActive: boolean,
  now: number = Date.now()
): CampaignWindowStatus {
  if (!isActive) return "inactive";
  if (Number.isFinite(startAtMs as number) && now < (startAtMs as number))
    return "upcoming";
  if (Number.isFinite(endAtMs as number) && now > (endAtMs as number))
    return "ended";
  return "active";
}
